import type _jquery from "jquery";
import { ref, provide } from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord
} from "@mcwd/util-vue";
//import { useMarketoForm } from "./use-marketo-form-instance.js";
import { isNil } from "@mcwd/typescript-type-guards";

import { useInputState } from "./use-input-state.js";
import { useSubmitState } from "./use-submit-state.js";
import { useFormSettings } from "./use-form-settings.js";
import { useMarketoFormInternals } from "./use-marketo-form-instance.js";
import { useMarketoRecaptcha } from "./use-marketo-recaptcha.js";
import { useGatingLevel } from "./use-gating-level.js";
import { useFormUserInputValues } from "./use-form-user-input-values.js";
import { useFormEvents } from "./use-form-events.js";
import { useFormLogger } from "./use-form-logger.js";

export interface FormSubmitState {
  isFormSubmitted?: boolean
}

export const formSubmitBool = ref(false);

type UseFormSubmitInjectionValues = InjectionValuesRecord<{
  submitForm: () => Promise<void>;
  errorCheck(): void;
}>;

type UseFormSubmitInjectionKeys = InjectionKeysRecord<UseFormSubmitInjectionValues>;

const UseFormSubmitInjectionKeys = CreateInjectionKeysRecord<UseFormSubmitInjectionKeys>({
  submitForm: "submitForm",
  errorCheck: "errorCheck",
});


export function initFormSubmit<EnabledFields extends string>() {
  if (checkValuesAlreadyProvided(UseFormSubmitInjectionKeys)) {
    return;
  }
  const { logger } = useFormLogger();

  const { CurrentMarketoFormGatingState } = useGatingLevel({ callerLabel: "initFormSubmit" });
  const { FormSettings } = useFormSettings({ callerLabel: "initFormSubmit" });
  const { SubmitMarketoForm } = useMarketoFormInternals({ callerLabel: "initFormSubmit" });
  const { inputStates } = useInputState<EnabledFields>({ callerLabel: "initFormSubmit" });
  const { submitState } = useSubmitState({ callerLabel: "initFormSubmit" });
  const { userInputValues } = useFormUserInputValues({ callerLabel: "iniFormSubmit" });

  const { setRecaptchaToken } = useMarketoRecaptcha({ callerLabel: "initFormSubmit" });
  const { validationErrorOnSubmit } = useFormEvents({ callerLabel: "initFormSubmit" });

  const userValuesIsEmpty = () => {
    return isNil(userInputValues) || Object.keys(userInputValues).length === 0;
  };

  // Stop the modal from closing if submit button was clicked
  function submitModalHold(holdFormSubmit: boolean) {
    formSubmitBool.value = holdFormSubmit;
  }

  async function submitForm() {
    try {
      logger.info("calling submit form");
      if (userValuesIsEmpty() && CurrentMarketoFormGatingState.value !== "BlindSubmit") {
        logger.warn("User provided FormValues is Nil in a Gated form");
        return;
      }
      submitState.setAllFieldsTouched();
  
      const visibleErrors = submitState.fieldsWithVisibleErrors;
      if (visibleErrors.length > 0) {
        validationErrorOnSubmit.trigger({ eventName: "validationErrorOnSubmit" }).catch(logger.error);
      }
  
      if (!submitState.submitReadyBool) {
        return;
      }
  
      if (FormSettings.hasRecaptcha) {
        console.log("Submitting form with recaptcha!");
        submitModalHold(true);
        await setRecaptchaToken();
        SubmitMarketoForm();
        logger.info("Form Submitted");
      } else {
        console.log("Submitting form without recaptcha!");
        submitModalHold(true);
        SubmitMarketoForm();
        logger.info("Form Submitted");
      }
      if (formSubmitBool.value) {
        setTimeout(() => {
          submitModalHold(false);
        }, 1000);
      }
    }
    catch (err) {
      logger.error("Error in form submission!", err);
    }
  }

  // If user hovers over button before form complete
  function doneCheck() {
    if (userValuesIsEmpty() && CurrentMarketoFormGatingState.value === "BlindSubmit") {
      return;
    }
    const requiredCount = submitState.totalRequired;
    const doneCount = submitState.totalDone;
    logger.debug({ doneCount, requiredCount });
    logger.debug({ "Done Fields": submitState.doneFields });
    logger.debug({ "Required Fields": submitState.requiredFields });
  }

  function checkValueIsEmpty(value: unknown) {
    return isNil(value) || (value === "");
  }

  // Checks if there are any errors before submit
  function errorCheck() {
    if (userValuesIsEmpty()) {
      if (CurrentMarketoFormGatingState.value === "BlindSubmit") {
        return;
      } else {
        logger.warn("User provided FormValues is Nil in a Gated form");
        return;
      }
    }
    doneCheck();
    type InputStateKey = keyof typeof inputStates;
    const inputStateKeys = Object.keys(inputStates) as InputStateKey[];

    inputStateKeys.forEach((key) => {
      const isRequired = inputStates[key].required === true;
      // if required then check for empty value in the formValues
      if (isRequired && checkValueIsEmpty(userInputValues[key])) {
        logger.info(`Required value "${key}" is empty`);
      }
    });
  }
  provide(UseFormSubmitInjectionKeys.submitForm, submitForm);
  provide(UseFormSubmitInjectionKeys.errorCheck, errorCheck);
}

export function useFormSubmit({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger({ callerLabel: "useFormSubmit" });
  try {
    callerLabel ??= "useFormSubmit";
    const submitForm = injectRequiredWithSelf(UseFormSubmitInjectionKeys.submitForm);
    const errorCheck = injectRequiredWithSelf(UseFormSubmitInjectionKeys.errorCheck);
    if (isNil(submitForm)) {
      throw new Error("submitForm is Nil");
    }
    if (isNil(errorCheck)) {
      throw new Error("errorCheck is Nil");
    }
    return { submitForm, errorCheck };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}
